import {useAgencyQuery, useAgencySubbrandsQuery} from "@/api/agencyApi";
import {useCompanyQuery} from "@/api/companyApi";
import {useIsAdmin, useIsAgency} from "@/hooks/useUserHooks";
import {getMissingProfileFields, useSettingsCompanyStore} from "@/store/company";
import type {TSettingsScope, TSettingsScopeRow, TSettingsScopeSection} from "@/store/types";
import useUserStore from "@/store/user";
import {keepPreviousData} from "@tanstack/react-query";

export const useSettingsScopeRows = ({scope}: {scope: TSettingsScope | null}) => {
  const user = useUserStore(state => state.user);
  const isAdmin = useIsAdmin();
  const isAgency = useIsAgency();
  const settingsCompany = useSettingsCompanyStore(state => state.company);

  const {data: scopeCompany} = useCompanyQuery({
    companyId: scope?.type === "brand" ? scope.id : 0,
    queryOptions: {placeholderData: keepPreviousData},
  });
  const scopeAgency = scope?.type === "brand" ? scopeCompany?.agency : undefined;
  const scopeAgencyId = scope?.type === "agency" ? scope.id : (scopeAgency?.multiple_brands && scopeAgency.id) || 0;
  const agencyId = isAdmin || isAgency ? scopeAgencyId : 0;

  const {data: agency} = useAgencyQuery({agencyId});
  const {data: agencySubBrands} = useAgencySubbrandsQuery({agencyId});

  const getRowMissingProfileFields = (profile: Parameters<typeof getMissingProfileFields>[0] & {id: number}) =>
    getMissingProfileFields(settingsCompany.id === profile.id ? settingsCompany : profile);

  const baseSections: TSettingsScopeSection[] = ["info", "users", "ambassadors"];
  const baseSectionsWithBilling: TSettingsScopeSection[] = [...baseSections, "billing"];
  const brandSections = agencyId ? baseSections : baseSectionsWithBilling;

  const agencyRows: TSettingsScopeRow[] = agency?.id
    ? [
        {
          missingProfileFields: [],
          name: agency.name,
          picture: agency.picture320 ?? agency.picture ?? null,
          scope: {id: agency.id, type: "agency"},
          sections: baseSectionsWithBilling,
        },
      ]
    : [];

  const ownBrandRows: TSettingsScopeRow[] = user.sub_brands.map(member => ({
    missingProfileFields: getRowMissingProfileFields(member.company),
    name: member.company.company_name ?? member.company.username ?? "",
    picture: member.company.picture320 ?? member.company.picture ?? null,
    scope: {id: member.company.id, type: "brand"},
    sections: brandSections,
  }));

  const isOwnBrandScope = scope?.type === "brand" && ownBrandRows.some(row => row.scope.id === scope.id);
  const scopeBrandRows: TSettingsScopeRow[] = scopeCompany
    ? [
        {
          missingProfileFields: getRowMissingProfileFields(scopeCompany),
          name: scopeCompany.company_name ?? scopeCompany.username ?? "",
          picture: scopeCompany.picture320 ?? scopeCompany.picture ?? null,
          scope: {id: scopeCompany.id, type: "brand"},
          sections: brandSections,
        },
      ]
    : [];

  const brandRows: TSettingsScopeRow[] = agencyId
    ? (agencySubBrands ?? []).map(brand => ({
        missingProfileFields: getRowMissingProfileFields(brand),
        name: brand.company_name ?? brand.username ?? "",
        picture: brand.picture320 ?? brand.picture ?? null,
        scope: {id: brand.id, type: "brand"},
        sections: brandSections,
      }))
    : isOwnBrandScope
      ? ownBrandRows
      : scopeBrandRows;

  const scopeRows = [...agencyRows, ...brandRows];
  const subscription = agency?.package_subscription ?? agency?.future_subscription;

  return {
    activeRow: scopeRows.find(row => row.scope.id === scope?.id && row.scope.type === scope?.type) ?? null,
    agency,
    agencyRows,
    brandRows,
    flatRow: scopeRows.length === 1 ? scopeRows[0] : null,
    seatsLeft: !subscription ? 0 : (subscription.number_of_seats_left ?? 100_000),
  };
};
